import { useState, useEffect, useRef, RefObject } from 'react';

interface MousePosition {
  x: number;
  y: number;
}

interface NormalizedMousePosition {
  x: number; // 0 to 1
  y: number; // 0 to 1
}

/**
 * Track mouse position relative to the viewport
 * Returns normalized values (0 to 1) for shader/animation use
 */
export function useMousePosition(): NormalizedMousePosition {
  const [position, setPosition] = useState<NormalizedMousePosition>({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      setPosition({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight
      });
    };

    window.addEventListener('mousemove', handleMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  return position;
}

/**
 * Track mouse position relative to a specific element
 * Returns values from -0.5 to 0.5 (center-relative)
 */
export function useMousePositionRelative(ref: RefObject<HTMLElement | null>): MousePosition {
  const [position, setPosition] = useState<MousePosition>({ x: 0, y: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleMove = (e: MouseEvent) => {
      const rect = element.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      setPosition({ x, y });
    };

    const handleLeave = () => {
      setPosition({ x: 0, y: 0 });
    };

    element.addEventListener('mousemove', handleMove, { passive: true });
    element.addEventListener('mouseleave', handleLeave);

    return () => {
      element.removeEventListener('mousemove', handleMove);
      element.removeEventListener('mouseleave', handleLeave);
    };
  }, [ref]);

  return position;
}

/**
 * Track mouse position with spring-smoothed values
 * Useful for magnetic effects where you want smooth following
 */
export function useMousePositionSmoothed(smoothing = 0.1): MousePosition {
  const [position, setPosition] = useState<MousePosition>({ x: 0, y: 0 });
  const targetRef = useRef<MousePosition>({ x: 0, y: 0 });
  const currentRef = useRef<MousePosition>({ x: 0, y: 0 });
  const rafRef = useRef<number>();

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      targetRef.current = {
        x: e.clientX,
        y: e.clientY
      };
    };

    const animate = () => {
      currentRef.current = {
        x: currentRef.current.x + (targetRef.current.x - currentRef.current.x) * smoothing,
        y: currentRef.current.y + (targetRef.current.y - currentRef.current.y) * smoothing
      };
      setPosition(currentRef.current);
      rafRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener('mousemove', handleMove, { passive: true });
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [smoothing]);

  return position;
}
